import React, { useState } from "react";
import cx from "classnames";
import styles from "../../scss/AFDialog.module.scss";
import LModal from "../../../common/components/Modals/LModal/LModal";
import Button from "../../../common/components/Forms/Button/Button";
import Input from "../../../common/components/Forms/Input/Input";
import Icon from "../../../common/components/Icon/Icon";
import { IContactInfo } from "../../interfaces/IAppFormData";
import { emailCB } from "../../api/ApiAppForm";
import useErrorLogger from "../../../common/hooks/useErrorLogger";
import * as Yup from "yup";
import { Form, Formik } from "formik";

interface IAFShareDialog {
    isOpen: boolean;
    onClose: (isContinue: boolean) => void;
    contacts?: IContactInfo;
    openTerms?: () => void;
}

const AFShareDialog: React.FC<IAFShareDialog> = (props) => {
    const { isOpen, onClose } = props;

    const initialValue = {
        friendOneName: "",
        friendOneEmail: "",
        friendTwoName: "",
        friendTwoEmail: "",
        clientName: "",
        clientEmail: "",
        clientMessage: ""
    };

    const validationSchema = Yup.object().shape({
        friendOneName: Yup.string().trim().required("This field is required"),
        friendOneEmail: Yup.string()
            .trim()
            .email("Invalid email")
            .required("This field is required"),
        clientName: Yup.string().trim().required("This field is required"),
        clientEmail: Yup.string()
            .trim()
            .email("Invalid email")
            .required("This field is required")
    });

    const handleSubmitForm = (values): void => {
        const { clientName, clientEmail, clientMessage } = values;

        const friends = [
            {
                firstName: values?.friendOneName,
                email: values?.friendOneEmail
            }
        ];

        if (values?.friendTwoName || values?.friendTwoEmail) {
            friends.push({
                firstName: values?.friendTwoName,
                email: values?.friendTwoEmail
            });
        }

        const paramsBody = {
            clientId: props?.contacts?.clientId,
            clientName,
            clientEmail,
            clientMessage,
            friends
        };

        emailCB(paramsBody)
            .catch(() => useErrorLogger())
            .finally(() => {
                onClose(true);
            });
    };

    return (
        <LModal
            Header={() => null}
            calcHeightMode="auto"
            width="narrow"
            removePadding={true}
            isOpen={isOpen}>
            {() => {
                return (
                    <div className={cx(styles["af-share-dialog-modal"])}>
                        <div className={styles["af-share__header"]}>
                            <h2>Tell a friend</h2>
                            <button
                                className={styles["af-share__close-btn"]}
                                onClick={() => onClose(false)}>
                                <Icon alt="close icon" id={"CloseModal"} size="l" />
                            </button>
                        </div>
                        <div className={styles["af-share__content"]}>
                            <p className={styles["af-share__desc"]}>
                                Share LifeDirect with your mates. If you refer a friend
                                and they take up insurance with us too you'll both receive
                                a $50 cashback.*
                            </p>

                            <p className={styles["af-share__note"]}>
                                We'll send a single email and don't store email addresses.
                            </p>
                        </div>
                        <Formik
                            initialValues={initialValue}
                            validationSchema={validationSchema}
                            onSubmit={(values) => handleSubmitForm(values)}>
                            {({
                                values,
                                handleChange,
                                setFieldValue,
                                handleBlur,
                                touched,
                                errors,
                                isValid,
                                isSubmitting
                            }) => (
                                <Form autoComplete="off">
                                    <div className={styles["af-share__form-wrapper"]}>
                                        <div className={styles["af-share__form"]}>
                                            <div
                                                className={
                                                    styles["af-share__form-header"]
                                                }>
                                                <h2>Friend 1</h2>
                                            </div>
                                            <Input
                                                className={cx("gap-ml")}
                                                id="friendOneName"
                                                name="friendOneName"
                                                type="text"
                                                value={values.friendOneName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label="First name"
                                                placeholder="First name"
                                                hasError={
                                                    touched.friendOneName &&
                                                    errors.friendOneName
                                                }
                                            />
                                            <Input
                                                className={cx("gap-ml")}
                                                id="friendOneEmail"
                                                name="friendOneEmail"
                                                type="text"
                                                value={values.friendOneEmail}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label="Email address"
                                                placeholder="Email address"
                                                hasError={
                                                    touched.friendOneEmail &&
                                                    errors.friendOneEmail
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className={styles["af-share__form-wrapper"]}>
                                        <div className={styles["af-share__form"]}>
                                            <div
                                                className={
                                                    styles["af-share__form-header"]
                                                }>
                                                <h2>Friend 2 (optional)</h2>
                                            </div>
                                            <Input
                                                className={cx("gap-ml")}
                                                id="friendTwoName"
                                                name="friendTwoName"
                                                type="text"
                                                value={values.friendTwoName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label="First name"
                                                placeholder="First name"
                                            />
                                            <Input
                                                className={cx("gap-ml")}
                                                id="friendTwoEmail"
                                                name="friendTwoEmail"
                                                type="text"
                                                value={values.friendTwoEmail}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label="Email address"
                                                placeholder="Email address"
                                            />
                                        </div>
                                    </div>
                                    <div className={styles["af-share__form-wrapper"]}>
                                        <div className={styles["af-share__form"]}>
                                            <div
                                                className={
                                                    styles["af-share__form-header"]
                                                }>
                                                <h2>Your details</h2>
                                            </div>
                                            <Input
                                                className={cx("gap-ml")}
                                                id="clientName"
                                                name="clientName"
                                                type="text"
                                                value={values.clientName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label="Your first name"
                                                placeholder="First name"
                                                hasError={
                                                    touched.clientName &&
                                                    errors.clientName
                                                }
                                            />
                                            <Input
                                                className={cx("gap-ml")}
                                                id="clientEmail"
                                                name="clientEmail"
                                                type="text"
                                                value={values.clientEmail}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                label="Your email address"
                                                placeholder="Email address"
                                                hasError={
                                                    touched.clientEmail &&
                                                    errors.clientEmail
                                                }
                                            />
                                        </div>
                                    </div>
                                    <div className={styles["af-share__form-wrapper"]}>
                                        <div className={styles["af-share__form"]}>
                                            <div
                                                className={
                                                    styles["af-share__form-header"]
                                                }>
                                                <h2>Personal message</h2>
                                            </div>
                                            <label
                                                className={styles["af-share__form-note"]}
                                                htmlFor="You can add an optional personal message">
                                                You can add an optional personal message
                                            </label>
                                            <Input
                                                className={cx("gap-m")}
                                                id="clientMessage"
                                                name="clientMessage"
                                                type="textarea"
                                                value={values.clientMessage}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value?.length > 500) return;
                                                    setFieldValue("clientMessage", value);
                                                }}
                                                onBlur={handleBlur}
                                            />
                                            <p className={styles["af-share__form-note"]}>
                                                {500 - values.clientMessage.length}{" "}
                                                characters remaining
                                            </p>
                                        </div>
                                    </div>
                                    <div className={styles["af-share__content"]}>
                                        <Button
                                            className={styles["af-share__continue"]}
                                            width="fill"
                                            component="button"
                                            size="s"
                                            type="submit"
                                            isDisabled={!isValid}
                                            isSubmitting={isSubmitting}
                                            variant="calltoaction">
                                            <span>Send Email</span>
                                        </Button>

                                        <Button
                                            className={styles["af-share__cancel"]}
                                            width="fill"
                                            component="p"
                                            size="s"
                                            onClick={() => onClose(false)}
                                            variant="default">
                                            <span>Cancel</span>
                                        </Button>

                                        <p
                                            className={cx(
                                                styles["af-share__note"],
                                                styles["af-terms"]
                                            )}
                                            onClick={() => props.openTerms()}>
                                            * Terms & conditions apply
                                        </p>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                );
            }}
        </LModal>
    );
};

export default AFShareDialog;
